import { useCallback, useRef, useState, useEffect } from "react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { ChevronDown, Upload, Paperclip, Trash2, Download, Loader2, File } from "lucide-react";
import { useBillAttachments, useUploadBillAttachment, useDeleteBillAttachment, BillAttachment } from "@/hooks/useBillAttachments";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const ACCEPTED = ".pdf,.png,.jpg,.jpeg,.heic,.docx,.xlsx,.csv";
const MAX_SIZE = 50 * 1024 * 1024; // 50 MB

const formatSize = (bytes: number | null) => {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

interface Props {
  billId: string;
}

const BillAttachments = ({ billId }: Props) => {
  const { data: attachments, isLoading } = useBillAttachments(billId);
  const upload = useUploadBillAttachment();
  const deleteMut = useDeleteBillAttachment();
  const fileRef = useRef<HTMLInputElement>(null);
  const dropRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handleFiles = useCallback(async (files: FileList | File[]) => {
    const arr = Array.from(files);
    if (!arr.length) return;
    setUploading(true);
    try {
      for (const file of arr) {
        if (file.size > MAX_SIZE) {
          toast.error(`${file.name} exceeds 50 MB limit`);
          continue;
        }
        await upload.mutateAsync({ bill_id: billId, file });
      }
      toast.success(`${arr.length} file${arr.length > 1 ? "s" : ""} uploaded`);
    } catch (e: any) {
      toast.error(e.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  }, [billId, upload]);

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer.files.length) handleFiles(e.dataTransfer.files);
  }, [handleFiles]);

  const onDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragging(true);
  }, []);

  const onDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
  }, []);

  // Paste support
  useEffect(() => {
    const handler = (e: ClipboardEvent) => {
      if (!dropRef.current?.closest("[data-bill-card]")?.contains(document.activeElement) &&
          !dropRef.current?.contains(document.activeElement)) return;
      const files = e.clipboardData?.files;
      if (files?.length) {
        e.preventDefault();
        handleFiles(files);
      }
    };
    document.addEventListener("paste", handler);
    return () => document.removeEventListener("paste", handler);
  }, [handleFiles]);

  const handleDownload = async (att: BillAttachment) => {
    const { data } = await supabase.storage.from("bill-attachments").createSignedUrl(att.file_path, 300);
    if (data?.signedUrl) window.open(data.signedUrl, "_blank");
    else toast.error("Could not generate download link");
  };

  const handleDelete = async (att: BillAttachment) => {
    try {
      await deleteMut.mutateAsync(att);
      toast.success("Attachment deleted");
    } catch {
      toast.error("Failed to delete attachment");
    }
  };

  const count = attachments?.length ?? 0;

  return (
    <Collapsible>
      <CollapsibleTrigger asChild>
        <Button variant="ghost" size="sm" className="w-full justify-between text-xs text-muted-foreground mt-1">
          <span className="flex items-center gap-1.5">
            <Paperclip className="h-3.5 w-3.5" />
            Attachments{count > 0 && ` (${count})`}
          </span>
          <ChevronDown className="h-3.5 w-3.5 transition-transform duration-200 [[data-state=open]_&]:rotate-180" />
        </Button>
      </CollapsibleTrigger>

      <CollapsibleContent className="pt-2 space-y-3">
        {/* Drop zone */}
        <div
          ref={dropRef}
          onDrop={onDrop}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          tabIndex={0}
          className={cn(
            "border-2 border-dashed rounded-lg p-4 text-center transition-colors cursor-pointer",
            dragging
              ? "border-primary bg-primary/5"
              : "border-muted-foreground/20 hover:border-muted-foreground/40"
          )}
          onClick={() => fileRef.current?.click()}
        >
          <input
            ref={fileRef}
            type="file"
            accept={ACCEPTED}
            multiple
            className="hidden"
            onChange={(e) => {
              if (e.target.files?.length) handleFiles(e.target.files);
              e.target.value = "";
            }}
          />
          {uploading ? (
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Uploading…
            </div>
          ) : (
            <div className="flex flex-col items-center gap-1">
              <Upload className="h-5 w-5 text-muted-foreground/60" />
              <p className="text-xs text-muted-foreground">
                Drop files, paste, or <span className="text-primary underline">browse</span>
              </p>
              <p className="text-[10px] text-muted-foreground/50">PDF, images, DOCX, XLSX, CSV · 50 MB max</p>
            </div>
          )}
        </div>

        {/* Attachment list */}
        {isLoading ? (
          <div className="flex justify-center py-2">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        ) : count > 0 ? (
          <div className="space-y-1.5">
            {attachments!.map((att) => (
              <div
                key={att.id}
                className="flex items-center justify-between gap-2 rounded-md border border-border bg-muted/30 px-3 py-2 text-sm"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <File className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <div className="min-w-0">
                    <p className="truncate font-medium text-xs">{att.file_name}</p>
                    <p className="text-[10px] text-muted-foreground">
                      {formatSize(att.file_size)} · {att.created_at?.split("T")[0]}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => handleDownload(att)}
                  >
                    <Download className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-destructive hover:text-destructive"
                    onClick={() => handleDelete(att)}
                    disabled={deleteMut.isPending}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : null}
      </CollapsibleContent>
    </Collapsible>
  );
};

export default BillAttachments;
